import { api } from './_client';
import type {
  Note,
  NoteCreateRequest,
  NoteUpdateRequest,
  NoteType,
  NotePriority,
} from '../../types/api';

export const notesApi = {
  // Create a new note
  async createNote(droneSerial: string, noteData: NoteCreateRequest): Promise<Note> {
    const response = await api.post<Note>(`/notes/${droneSerial}`, noteData);
    return response.data;
  },

  // Get notes for a drone
  async getNotes(
    droneSerial: string,
    params?: {
      limit?: number;
      start_date?: string;
      end_date?: string;
      note_type?: NoteType;
      priority?: NotePriority;
      author?: string;
      include_resolved?: boolean;
    }
  ): Promise<Note[]> {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const url = `/notes/${droneSerial}${
      searchParams.toString() ? `?${searchParams.toString()}` : ''
    }`;
    const response = await api.get<Note[]>(url);
    return response.data;
  },

  // Get notes for timeline
  async getTimelineNotes(droneSerial: string, startDate: string, endDate: string): Promise<Note[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    const response = await api.get<Note[]>(`/notes/${droneSerial}/timeline?${params.toString()}`);
    return response.data;
  },

  // Get notes date range for a drone (lightweight backend aggregate)
  async getNotesDateRange(
    droneSerial: string
  ): Promise<{ earliest_date: string | null; latest_date: string | null }> {
    try {
      const response = await api.get<{
        earliest_date: string | null;
        latest_date: string | null;
      }>(`/notes/${droneSerial}/date-range`);
      return response.data;
    } catch (error) {
      console.error('API: getNotesDateRange failed:', error);
      return { earliest_date: null, latest_date: null };
    }
  },

  // Update a note
  async updateNote(droneSerial: string, id: string, updates: NoteUpdateRequest): Promise<Note> {
    const response = await api.put<Note>(`/notes/${droneSerial}/${id}`, updates);
    return response.data;
  },

  // Resolve a note
  async resolveNote(droneSerial: string, id: string, resolutionNote?: string): Promise<Note> {
    const params = resolutionNote ? `?resolution_note=${encodeURIComponent(resolutionNote)}` : '';
    const response = await api.post<Note>(`/notes/${droneSerial}/${id}/resolve${params}`);
    return response.data;
  },

  // Unresolve a note
  async unresolveNote(droneSerial: string, id: string): Promise<Note> {
    const response = await api.post<Note>(`/notes/${droneSerial}/${id}/unresolve`);
    return response.data;
  },

  // Delete a note
  async deleteNote(droneSerial: string, id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/notes/${droneSerial}/${id}`);
    return response.data;
  },

  // Search notes
  async searchNotes(query: string, droneSerial?: string, limit?: number): Promise<Note[]> {
    const params = new URLSearchParams({ query });
    if (droneSerial) params.append('drone_serial', droneSerial);
    if (limit) params.append('limit', limit.toString());

    const response = await api.get<Note[]>(`/notes/search?${params.toString()}`);
    return response.data;
  },

  // Update note date
  async updateNoteDate(droneSerial: string, noteId: string, dateOfAction: string): Promise<Note> {
    const params = new URLSearchParams({ date_of_action: dateOfAction });
    const response = await api.patch<Note>(
      `/notes/${droneSerial}/${noteId}/date?${params.toString()}`
    );
    return response.data;
  },

  // Update note content
  async updateNoteContent(
    droneSerial: string,
    noteId: string,
    subject?: string,
    content?: string
  ): Promise<Note> {
    const params = new URLSearchParams();
    if (subject) params.append('subject', subject);
    if (content) params.append('content', content);
    const response = await api.patch<Note>(
      `/notes/${droneSerial}/${noteId}/content?${params.toString()}`
    );
    return response.data;
  },

  // Upload attachment
  async uploadAttachment(file: File, droneSerial: string): Promise<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('drone_serial', droneSerial);

    const response = await api.post<{ url: string; key: string }>(`/attachments/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // Increase timeout for file uploads
    });

    return response.data;
  },

  // Get a fresh presigned download URL for an attachment by its S3 key
  async getAttachmentDownloadUrl(s3Key: string): Promise<{ download_url: string }> {
    const response = await api.get<{ download_url: string }>(
      `/attachments/${encodeURIComponent(s3Key)}/download`
    );
    return response.data;
  },
};
